import React, { useState, useEffect, useRef, useMemo } from "react";
import Chart from "@qognicafinance/react-lightweight-charts";
import { useChart } from "@stores/chart.store";
import { usePools } from "@stores/pools.store";
import { useSwap } from "@stores/swap.store";
import { TimePeriodConfig, timePeriods as timePeriodConfigs } from "./TimeSwitch";

interface ICandlestickData {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
}

// Sample data for visualization
const sampleData: ICandlestickData[] = [
    { time: Math.floor(Date.now() / 1000) - 86400 * 5, open: 0, high: 0, low: 0, close: 0 },
];

const defaultCandlestickSeries = [
    {
        data: sampleData,
        options: {
            upColor: "#57E18E",
            downColor: "#8080FF",
            borderUpColor: "#57E18E",
            borderDownColor: "#8080FF",
            wickUpColor: "#57E18E",
            wickDownColor: "#8080FF",
        },
    },
];

const TradingView: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 300, height: 200 });

    const {
        chartData,
        isLoading,
        error: chartError,
        updateChartData,
        selectedTimePeriod,
        chartOptions,
        visibleRange,
    } = useChart();
    const { pools, error: poolsError } = usePools();
    const { sellAssetInfo, buyAssetInfo, selectedPool } = useSwap();
    const [hasFetched, setHasFetched] = useState(false);

    // Get selected time period configuration
    const timePeriodConfig = timePeriodConfigs.find((tp: TimePeriodConfig) => tp.label === selectedTimePeriod);

    // Compute time range
    const defaultFrom = useMemo(() => {
        if (!timePeriodConfig) return Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60; // Default to 1 week (7 days)
        return Math.floor(Date.now() / 1000) - timePeriodConfig.datasetSize;
    }, [timePeriodConfig]);

    const defaultTo = useMemo(() => Math.floor(Date.now() / 1000), []);

    const options = useMemo(
        () => ({
            alignLabels: true,
            timeScale: {
                rightOffset: 0,
                barSpacing: 6,
                lockVisibleTimeRangeOnResize: false,
                rightBarStaysOnScroll: true,
                borderVisible: false,
                borderColor: "#444444",
                visible: true,
                timeVisible: true,
                secondsVisible: false,
                tickMarkStep: 1,
                tickMarkFormatter:
                    chartOptions.timeScale?.tickMarkFormatter ||
                    ((time: number): string => {
                        const date = new Date(time * 1000);
                        return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
                    }),
                minBarSpacing: 1,
            },
            localization: {
                dateFormat: chartOptions.localization.dateFormat,
                timeFormatter: chartOptions.localization.timeFormatter,
            },
            priceScale: {
                scaleMargins: {
                    top: 0.1,
                    bottom: 0.1,
                },
                borderVisible: false,
            },
            layout: {
                backgroundColor: "transparent",
                textColor: "#ffffff",
            },
            grid: {
                vertLines: { color: "#313339", style: 3 },
                horzLines: { color: "#313339", style: 3 },
            },
        }),
        [timePeriodConfig, chartOptions],
    );

    const [state, setState] = useState({
        options,
        candlestickSeries: defaultCandlestickSeries,
        from: defaultFrom,
        to: defaultTo,
    });

    // Update chart options when time period changes
    useEffect(() => {
        setState((prev) => ({
            ...prev,
            options: {
                ...prev.options,
                timeScale: {
                    ...prev.options.timeScale,
                    tickMarkFormatter:
                        chartOptions.timeScale?.tickMarkFormatter ||
                        ((time: number): string => {
                            const date = new Date(time * 1000);
                            return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
                        }),
                    timeVisible: true,
                    secondsVisible: false,
                },
                localization: {
                    dateFormat: chartOptions.localization.dateFormat,
                    timeFormatter: chartOptions.localization.timeFormatter,
                },
            },
        }));
    }, [timePeriodConfig, chartOptions]);

    useEffect(() => {
        if (chartData?.chartData?.length) {
            setState((prev) => ({
                ...prev,
                candlestickSeries: [{ ...defaultCandlestickSeries[0], data: [...chartData.chartData] }], //
            }));
        }
    }, [chartData]);

    useEffect(() => {
        if (sellAssetInfo && buyAssetInfo && selectedPool && pools?.length) {
            const pool = pools.find((p) => p.dex === selectedPool);
            if (pool && timePeriodConfig) {
                // Only fetch data if we haven't fetched yet
                if (!hasFetched) {
                    setHasFetched(true);
                    const resolution = timePeriodConfig.resolution;

                    updateChartData(
                        pool,
                        [sellAssetInfo.asset, buyAssetInfo.asset],
                        resolution,
                        defaultFrom,
                        defaultTo,
                        visibleRange,
                    ).catch(console.error);
                }
            }
        }
    }, [
        sellAssetInfo,
        buyAssetInfo,
        selectedPool,
        pools,
        updateChartData,
        defaultFrom,
        defaultTo,
        timePeriodConfig,
        visibleRange,
    ]);

    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current?.parentElement) {
                setDimensions({
                    width: Math.max(containerRef.current.parentElement.clientWidth, 300),
                    height: Math.max(containerRef.current.parentElement.clientHeight, 200),
                });
            }
        };

        if (containerRef.current?.parentElement) {
            updateDimensions();
            const observer = new ResizeObserver(updateDimensions);
            observer.observe(containerRef.current);
            window.addEventListener("resize", updateDimensions);

            return () => {
                window.removeEventListener("resize", updateDimensions);
                observer.disconnect();
            };
        }
    }, []);

    const showError = poolsError === "Invalid API-KEY" || chartError;
    const errorMessage = poolsError || chartError || "Unable to load chart data";

    return (
        <div ref={containerRef} className="w-full h-full relative text-card-foreground" style={{ minHeight: "300px" }}>
            {showError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/10 z-10 p-4"></div>
            )}
            <Chart
                width={dimensions.width}
                height={dimensions.height}
                darkTheme
                options={state.options}
                autoWidth
                autoHeight
                candlestickSeries={state.candlestickSeries}
                from={visibleRange}
                to={defaultTo}
            />
        </div>
    );
};

export default TradingView;
